import { useState, useEffect, useMemo, useRef, useCallback, type KeyboardEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, MapPin, LocateFixed } from 'lucide-react'
import { Container } from '@/components/layout/Container'
import { CitySelector } from '@/components/layout/CitySelector'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SearchSuggestionsDropdown } from '@/components/search/SearchSuggestionsDropdown'
import { HERO_TABS, heroTabScope, heroTabPremium, heroTabCategory } from '@/lib/site'
import { getSearchSuggestions, type SearchSuggestion } from '@/lib/searchSuggestions'
import { useSearchSuggestions } from '@/hooks/api/useSmartSearch'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useCities } from '@/hooks/api/useCities'
import { useFilterOptions } from '@/hooks/api/useFilterOptions'
import { apiSuggestionsToList } from '@/lib/apiSuggestions'
import { useCityStore } from '@/store/cityStore'
import { useRecentSearchStore } from '@/store/recentSearchStore'
import { HeroQuickLinks } from '@/components/home/HeroQuickLinks'
import { HeroMobileServices } from '@/components/home/HeroMobileServices'
import { useUiStore } from '@/store/uiStore'
import { markSearchPerformed } from '@/lib/analytics/searchEvents'
import { buildPropertiesSearchUrl, extractIntentFromText } from '@/lib/parseSearchQuery'
import { cn } from '@/lib/utils'
import { trackPostPropertyClick } from '@/lib/analytics/sellerEvents'
import { containsTarget } from '@/lib/dom'

const HERO_IMAGE_BASE = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9'
const heroImageUrl = (width: number) => `${HERO_IMAGE_BASE}?w=${width}&fit=crop&auto=format&q=70`
const HERO_IMAGE_SRCSET = [640, 960, 1280, 1600].map((w) => `${heroImageUrl(w)} ${w}w`).join(', ')

/** LCP element: one shared srcset so the mobile + desktop copies resolve to a single download. */
function HeroImage() {
  return (
    <img
      src={heroImageUrl(1280)}
      srcSet={HERO_IMAGE_SRCSET}
      sizes="100vw"
      width={1600}
      height={900}
      alt=""
      fetchPriority="high"
      loading="eager"
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover"
    />
  )
}

export function HeroSearch() {
  const [activeTab, setActiveTab] = useState('buy')
  const [query, setQuery] = useState('')
  const [cityIndex, setCityIndex] = useState(0)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { cityName } = useCityStore()
  const { add: addRecent } = useRecentSearchStore()
  const { setSearchOpen } = useUiStore()
  const { request: requestLocation } = useGeolocation()
  const { cities: listedCities } = useCities({ withListings: true })
  const { options: filterOptions } = useFilterOptions(cityName)
  const knownAreas = useMemo(
    () => (filterOptions.areas ?? []).map((a) => a.name).filter(Boolean),
    [filterOptions.areas],
  )

  const rotatingCities = useMemo(() => {
    const names = listedCities
      .filter((c) => (c.propertyCount ?? 0) > 0)
      .sort((a, b) => (b.propertyCount ?? 0) - (a.propertyCount ?? 0))
      .slice(0, 5)
      .map((c) => c.name)
    return names.length ? names : [cityName || 'Rajkot']
  }, [listedCities, cityName])

  const tab = HERO_TABS.find((t) => t.id === activeTab) ?? HERO_TABS[0]
  // Prefer intent words typed in the query (e.g. "for Rent") over the Buy tab.
  const effectiveIntent = useMemo(() => {
    const q = query.trim()
    if (!q) return tab.intent
    const extracted = extractIntentFromText(q, tab.intent)
    return extracted.explicit ? extracted.intent : tab.intent
  }, [query, tab.intent])
  const tabScope = useMemo(
    () => ({ ...heroTabScope(tab), intent: effectiveIntent }),
    [tab, effectiveIntent],
  )
  const displayCity = cityName || rotatingCities[cityIndex % rotatingCities.length]
  // While rotating, reserve the widest city name so the heading never re-centres (layout shift).
  const cityMinWidth = cityName
    ? undefined
    : `${Math.max(displayCity.length, ...rotatingCities.map((name) => name.length))}ch`
  const isTyping = query.trim().length > 0
  const suggestionsOpen = showSuggestions && isTyping

  const { flat: apiFlat } = useSearchSuggestions(query, cityName, tabScope)

  const suggestionOpts = useMemo(
    () => ({
      intent: effectiveIntent,
      city: cityName,
      premium: heroTabPremium(tab),
      category: heroTabCategory(tab),
      query,
    }),
    [tab, cityName, query, effectiveIntent],
  )

  const suggestions = useMemo(() => {
    const live = apiSuggestionsToList(apiFlat, suggestionOpts)
    if (live.length > 0) return live

    if (!query.trim()) return []

    // Never show demo/mock projects while typing — only real API hits or safe fallbacks.
    return getSearchSuggestions({
      query,
      intent: tab.intent,
      city: cityName,
      premium: heroTabPremium(tab),
      category: heroTabCategory(tab),
      includeMockInventory: false,
    })
  }, [apiFlat, suggestionOpts, query, tab, cityName])

  useEffect(() => {
    if (cityName || rotatingCities.length < 2) return
    const timer = setInterval(() => {
      setCityIndex((i) => (i + 1) % rotatingCities.length)
    }, 3000)
    return () => clearInterval(timer)
  }, [cityName, rotatingCities.length])

  useEffect(() => {
    setActiveIndex(0)
    setShowSuggestions(false)
  }, [cityName, activeTab])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, activeTab, suggestions.length])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containsTarget(searchWrapRef.current, event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [])

  const handleSearch = useCallback(
    (searchQuery = query) => {
      const href = buildPropertiesSearchUrl(searchQuery, {
        intent: tab.intent,
        defaultCity: cityName,
        premium: heroTabPremium(tab),
        tabCategory: heroTabCategory(tab),
        knownAreas,
      })

      addRecent({ label: searchQuery || `${tab.label} in ${cityName}`, href })
      markSearchPerformed('hero')
      setShowSuggestions(false)
      navigate(href)
    },
    [query, tab, cityName, knownAreas, addRecent, navigate],
  )

  const handleSelectSuggestion = useCallback(
    (suggestion: SearchSuggestion) => {
      addRecent({ label: suggestion.title, href: suggestion.href })
      markSearchPerformed('hero')
      setShowSuggestions(false)
      navigate(suggestion.href)
    },
    [addRecent, navigate],
  )

  const detectLocation = () => {
    void requestLocation()
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (suggestionsOpen && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }
      if (event.key === 'Escape') {
        setShowSuggestions(false)
        return
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      // Always search the typed query. Suggestion rows can inherit a random
      // project locality on repeat searches (e.g. "new project").
      handleSearch()
    }
  }

  return (
    <section className="relative bg-page md:bg-neutral-50">
      <HeroMobileServices />

      {/* Mobile hero + floating search */}
      <div className="relative md:hidden">
        <div className="relative h-[200px] overflow-hidden">
          <HeroImage />
          <div className="absolute inset-0 bg-linear-to-b from-qbc-navy/50 via-primary-900/35 to-primary-900/55" />

          <Container className="relative flex h-full flex-col items-center justify-center px-4">
            <h1 className="text-center font-display text-[17px] font-bold leading-snug text-white">
              Find Your Dream Home{' '}
              <span className="block text-primary-100">
                in{' '}
                <span className="relative inline-block text-left" style={{ minWidth: cityMinWidth }}>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={displayCity}
                      initial={{ y: 6, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -6, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="inline-block border-b-2 border-primary-300/90 pb-px"
                    >
                      {displayCity}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </span>
            </h1>
          </Container>
        </div>

        <Container className="relative z-10 -mt-6 px-4">
          <div className="flex h-[52px] w-full items-center gap-2 rounded-xl bg-white px-2 shadow-[0_4px_24px_rgba(8,50,84,0.14)]">
            <CitySelector variant="hero" />
            <span className="h-6 w-px shrink-0 bg-neutral-200" aria-hidden />
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex min-w-0 flex-1 items-center gap-2.5 px-1"
            >
              <Search className="h-[18px] w-[18px] shrink-0 text-neutral-400" strokeWidth={2} />
              <span className="min-w-0 flex-1 truncate text-left text-[14px] text-neutral-500">
                Search locality, project...
              </span>
            </button>
            <button
              type="button"
              onClick={detectLocation}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600 transition-colors hover:bg-primary-100"
              aria-label="Search near me"
            >
              <LocateFixed className="h-[18px] w-[18px]" strokeWidth={2} />
            </button>
          </div>
        </Container>
      </div>

      {/* Desktop hero + search card */}
      <div className="hidden md:block">
        <div className="relative h-[44vh] min-h-[280px] max-h-[400px]">
          <HeroImage />
          <div className="absolute inset-0 bg-linear-to-b from-qbc-navy/85 via-primary-800/70 to-primary-900/50" />

          <Container className="relative flex h-full items-end justify-center px-4 pb-24 pt-6 text-center">
            <div className="max-w-4xl pb-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-200">
                Gujarat&apos;s #1 Property Platform
              </p>
              <h1 className="mt-2 font-display text-4xl font-extrabold leading-snug tracking-tight text-white">
                <span className="whitespace-nowrap">Find Your Dream Home</span>{' '}
                <span className="inline-flex h-[1.15em] items-baseline gap-2 whitespace-nowrap text-primary-200">
                  in
                  <span className="relative inline-block overflow-hidden text-left" style={{ minWidth: cityMinWidth }}>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={displayCity}
                        initial={{ y: 8, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -8, opacity: 0 }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                        className="relative inline-block pb-0.5"
                      >
                        {displayCity}
                        <span
                          className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary-300/80"
                          aria-hidden
                        />
                      </motion.span>
                    </AnimatePresence>
                  </span>
                </span>
              </h1>
              <p className="mx-auto mt-2 max-w-xl pb-3 text-sm leading-relaxed text-white/80">
                Buy, sell, or rent residential & commercial properties. Verified listings, premium projects & expert support.
              </p>
            </div>
          </Container>
        </div>

        <Container className="relative z-10 -mt-24 px-4">
          <div className="mx-auto max-w-5xl overflow-visible">
            <div className="overflow-visible rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.1)]">
              <div className="flex items-center justify-between gap-0.5 overflow-x-auto border-b border-neutral-100 px-4 pt-1.5 hide-scrollbar">
                <div className="flex items-center">
                  {HERO_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveTab(t.id)}
                      className={cn(
                        'relative shrink-0 px-4 py-2.5 text-sm font-semibold transition-colors',
                        activeTab === t.id
                          ? 'text-primary-600 after:absolute after:bottom-0 after:left-2 after:right-2 after:h-0.5 after:rounded-full after:bg-primary-500'
                          : 'text-neutral-500 hover:text-neutral-800',
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <Link
                  to="/add-property"
                  onClick={() => trackPostPropertyClick('home_hero')}
                  className="flex shrink-0 items-center gap-1 px-2 py-1.5 text-xs font-semibold text-success-600 hover:text-success-700"
                >
                  Post Property{' '}
                  <span className="rounded bg-success-500 px-1 py-0.5 text-[9px] font-bold text-white">FREE</span>
                </Link>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                  <CitySelector variant="hero" className="w-full sm:w-auto" />
                  <div ref={searchWrapRef} className="relative min-w-0 flex-1">
                    <MapPin className="absolute left-2.5 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-primary-400" />
                    <Input
                      placeholder={`Search locality, project in ${cityName}...`}
                      className={cn(
                        'h-11 border-neutral-200 pl-10 pr-10',
                        suggestionsOpen && 'rounded-b-none border-primary-300 ring-2 ring-primary-100',
                      )}
                      value={query}
                      onChange={(e) => {
                        const value = e.target.value
                        setQuery(value)
                        setShowSuggestions(value.trim().length > 0)
                      }}
                      onKeyDown={onInputKeyDown}
                      autoComplete="off"
                      role="combobox"
                      aria-expanded={suggestionsOpen}
                      aria-controls="search-suggestions"
                    />
                    <button
                      type="button"
                      onClick={detectLocation}
                      className="absolute right-1.5 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-primary-50 text-primary-600 transition-colors hover:bg-primary-100"
                      aria-label="Search near me"
                    >
                      <LocateFixed className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>

                    <SearchSuggestionsDropdown
                      open={suggestionsOpen}
                      suggestions={suggestions}
                      query={query}
                      activeIndex={activeIndex}
                      onSelect={handleSelectSuggestion}
                      onHoverIndex={setActiveIndex}
                      className="rounded-t-none border-t-0"
                    />
                  </div>

                  <Button className="h-11 w-full shrink-0 px-8 lg:w-auto" onClick={() => handleSearch()}>
                    <Search className="h-5 w-5" /> Search
                  </Button>
                </div>
              </div>

              <HeroQuickLinks intent={tab.intent} category={heroTabCategory(tab)} />
            </div>
          </div>
        </Container>
      </div>
    </section>
  )
}
